import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MetricBar } from "@/components/MetricBar";
import { EngagementTable } from "@/components/EngagementTable";
import { EngagementDrawer } from "@/components/EngagementDrawer";
import { ImportDialog } from "@/components/ImportDialog";
import { API, STATUSES } from "@/lib/engagements";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = user?.role !== "supervisor";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pics, setPics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [pic, setPic] = useState("");
  const [status, setStatus] = useState("");
  const [service, setService] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s, p] = await Promise.all([
        axios.get(`${API}/engagements`, { params: { q, pic, status, service } }),
        axios.get(`${API}/stats`),
        axios.get(`${API}/pics`),
      ]);
      setItems(list.data);
      setStats(s.data);
      setPics(p.data);
    } finally {
      setLoading(false);
    }
  }, [q, pic, status, service]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-3xl font-extrabold">Engagements</h2>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              data-testid="import-excel-btn"
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="rounded-sm gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" /> Import Excel
            </Button>
          )}
          {canEdit && (
            <Button
              data-testid="add-engagement-btn"
              onClick={() => {
                setSelected(null);
                setDrawerOpen(true);
              }}
              className="rounded-sm gap-2 bg-black text-white hover:bg-zinc-800 hover:text-white"
            >
              <Plus className="h-4 w-4" /> New engagement
            </Button>
          )}
        </div>
      </div>

      <MetricBar stats={stats} active={status} onSelect={setStatus} />

        <div className="flex flex-wrap items-center gap-3 border border-border bg-white p-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search client or year end…"
              className="rounded-sm pl-9"
            />
          </div>
          <select
            data-testid="filter-pic"
            value={pic}
            onChange={(e) => setPic(e.target.value)}
            className="h-10 rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">All PICs</option>
            {pics.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            data-testid="filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-10 rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select
            data-testid="filter-service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="h-10 rounded-sm border border-input bg-white px-3 text-sm"
          >
            <option value="">All services</option>
            <option value="account">Account only</option>
            <option value="audit">Audit applicable</option>
            <option value="tax">Tax applicable</option>
          </select>
          <div
            data-testid="result-count"
            className="ml-auto text-xs uppercase tracking-widest text-zinc-500"
          >
            {items.length} of {stats?.total ?? 0} engagements
          </div>
        </div>

        <EngagementTable
          items={items}
          loading={loading}
          onRowClick={(e) => {
            setSelected(e);
            setDrawerOpen(true);
          }}
        />
      <EngagementDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        engagement={selected}
        onSaved={load}
      />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
    </div>
  );
}
